import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE } from '../config/apiBase';

// Base URL for API requests
const API_BASE_URL = API_BASE;

const attachAuthInterceptors = (instance) => {
  instance.interceptors.request.use(
    async (config) => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken(/* forceRefresh */ false);
          if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`;
            return config;
          }
        }
      } catch (_) {
        // fall back to stored token
      }

      const stored = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (stored) {
        config.headers.Authorization = `Bearer ${stored}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const createAuthedClient = (baseURL) => attachAuthInterceptors(
  axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  })
);

// Dedicated clients for admin management endpoints and self-service endpoints
const userManagementAPI = createAuthedClient(`${API_BASE_URL}/user-management`);
const usersAPI = createAuthedClient(`${API_BASE_URL}/users`);

// Backwards compatibility alias
const userAPI = userManagementAPI;

/**
 * User API Service
 * Handles all user-related API operations
 */
class UserAPIService {
  
  // Get all users (admin only)
  async getAllUsers(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.search) params.append('search', filters.search);
      if (filters.role && filters.role !== 'all') params.append('role', filters.role);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      
      const response = await userAPI.get(`/all?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch users');
    }
  }

  // Get user by ID
  async getUserById(userId) {
    try {
      const response = await userAPI.get(`/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch user details');
    }
  }

  // Get current user profile
  async getCurrentUser() {
    try {
      const response = await usersAPI.get('/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch user profile');
    }
  }

  // Create new user
  async createUser(userData) {
    try {
      const response = await userAPI.post('/', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to create user');
    }
  }

  // Update user profile
  async updateUser(userId, userData) {
    try {
      const response = await userAPI.put(`/${userId}`, userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update user');
    }
  }

  // Update current user profile
  async updateProfile(userData) {
    try {
      const response = await usersAPI.put('/profile', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update profile');
    }
  }

  // Delete current user profile
  async deleteProfile() {
    try {
      const response = await usersAPI.delete('/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to delete profile');
    }
  }

  // Get authenticated user's preferences
  async getCurrentPreferences() {
    try {
      const response = await usersAPI.get('/preferences');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to load preferences');
    }
  }

  // Update authenticated user's preferences
  async updateCurrentPreferences(preferences) {
    try {
      const response = await usersAPI.put('/preferences', { preferences });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update preferences');
    }
  }

  // Get authenticated user's bookings
  async getCurrentBookings() {
    try {
      const response = await usersAPI.get('/bookings');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to load bookings');
    }
  }

  async getCurrentBookingDetails(bookingId) {
    try {
      const response = await usersAPI.get(`/bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to load booking details');
    }
  }

  async getCurrentReviews() {
    try {
      const response = await usersAPI.get('/reviews');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to load reviews');
    }
  }

  async submitReview(reviewData) {
    try {
      const response = await usersAPI.post('/reviews', reviewData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to submit review');
    }
  }

  // Update user role (admin only)
  async updateUserRole(userId, role) {
    try {
      const response = await userAPI.put(`/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update user role');
    }
  }

  // Update user status (admin only)
  async updateUserStatus(userId, status) {
    try {
      const response = await userAPI.put(`/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update user status');
    }
  }

  // Delete user (admin only)
  async deleteUser(userId) {
    try {
      const response = await userAPI.delete(`/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to delete user');
    }
  }

  // Bulk delete users (admin only)
  async bulkDeleteUsers(userIds) {
    try {
      const response = await userAPI.delete('/bulk', { data: { userIds } });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to delete users');
    }
  }

  // Activate user account
  async activateUser(userId) {
    try {
      const response = await userAPI.put(`/${userId}/activate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to activate user');
    }
  }

  // Deactivate user account
  async deactivateUser(userId) {
    try {
      const response = await userAPI.put(`/${userId}/deactivate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to deactivate user');
    }
  }

  // Send password reset email
  async sendPasswordReset(email) {
    try {
      const response = await userAPI.post('/password-reset', { email });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to send password reset email');
    }
  }

  // Verify user email
  async verifyEmail(userId, verificationToken) {
    try {
      const response = await userAPI.post(`/${userId}/verify-email`, { token: verificationToken });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to verify email');
    }
  }

  // Get user statistics (admin only)
  async getUserStats() {
    try {
      const response = await userAPI.get('/stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch user statistics');
    }
  }

  // Get user activity log
  async getUserActivity(userId, limit = 50) {
    try {
      const response = await userAPI.get(`/${userId}/activity?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch user activity');
    }
  }

  // Search users
  async searchUsers(query) {
    try {
      const response = await userAPI.get(`/search?q=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to search users');
    }
  }

  // Get user preferences
  async getUserPreferences(userId) {
    try {
      const response = await userAPI.get(`/${userId}/preferences`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch user preferences');
    }
  }

  // Update user preferences
  async updateUserPreferences(userId, preferences) {
    try {
      const response = await userAPI.put(`/${userId}/preferences`, { preferences });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to update user preferences');
    }
  }

  // Export users to CSV (admin only)
  async exportUsers(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.role && filters.role !== 'all') params.append('role', filters.role);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      
      const response = await userAPI.get(`/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Users exported successfully' };
    } catch (error) {
      throw this.handleError(error, 'Failed to export users');
    }
  }

  // Upload users from CSV (admin only)
  async importUsers(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await userAPI.post('/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to import users');
    }
  }

  // Send notification to user
  async sendNotification(userId, notification) {
    try {
      const response = await userAPI.post(`/${userId}/notifications`, notification);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to send notification');
    }
  }

  // Error handling helper
  handleError(error, defaultMessage) {
    console.error('User API Error:', error);
    
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request data');
        case 401:
          return new Error('Authentication required');
        case 403:
          return new Error('Access denied');
        case 404:
          return new Error('User not found');
        case 409:
          return new Error(data.message || 'User already exists');
        case 422:
          return new Error(data.message || 'Validation failed');
        case 500:
          return new Error('Server error. Please try again later.');
        default:
          return new Error(data.message || defaultMessage);
      }
    } else if (error.request) {
      // Network error
      return new Error('Network error. Please check your connection.');
    } else {
      // Other error
      return new Error(defaultMessage);
    }
  }
}

// Export singleton instance
export default new UserAPIService();

// Named exports for specific functions
export const {
  getAllUsers,
  getUserById,
  getCurrentUser,
  createUser,
  updateUser,
  updateProfile,
  deleteProfile,
  updateUserRole,
  updateUserStatus,
  deleteUser,
  bulkDeleteUsers,
  activateUser,
  deactivateUser,
  sendPasswordReset,
  verifyEmail,
  getUserStats,
  getUserActivity,
  searchUsers,
  getUserPreferences,
  updateUserPreferences,
  getCurrentPreferences,
  updateCurrentPreferences,
  getCurrentBookings,
  getCurrentBookingDetails,
  getCurrentReviews,
  submitReview,
  exportUsers,
  importUsers,
  sendNotification
} = new UserAPIService();